import type { ShopProduct } from "./shop-queries";

/**
 * The Shop's rules that need no network (ADR 0047 step 2): which ways an order can reach the member, and what
 * shipping costs. Kept apart from the queries so they are tested without the portal's configuration.
 */

/** How this church hands over what members buy: pickup, shipping and its rate, and whether tax is added. */
export interface StoreOptions {
  /** Where the church's members shop (ADR 0047 step 4). */
  readonly mode: "how" | "shopify" | "none";
  readonly shopifyUrl: string | null;
  readonly pickup: boolean;
  readonly pickupNote: string | null;
  readonly shipping: boolean;
  readonly shippingRateCents: number;
  readonly freeShippingOverCents: number | null;
  readonly shipCountries: readonly string[];
  readonly tax: boolean;
}

/** The church's shipping for items worth this much: its rate, or nothing over its free-shipping amount. */
export function shippingFor(options: StoreOptions, itemsCents: number): number {
  return options.freeShippingOverCents !== null && itemsCents >= options.freeShippingOverCents
    ? 0
    : options.shippingRateCents;
}

/** How an order reaches the member. */
export type Way = "pickup" | "ship";

/**
 * Which ways the cart can reach the member: what the church offers, less what a product rules out (a meal ticket is
 * picked up only, a mailed print is shipped only). Empty when the cart mixes the two.
 */
export function waysFor(options: StoreOptions, products: readonly ShopProduct[]): Way[] {
  const ways: Way[] = [];
  if (options.pickup && products.every((p) => p.fulfilment !== "ship")) ways.push("pickup");
  if (options.shipping && products.every((p) => p.fulfilment !== "pickup")) ways.push("ship");
  return ways;
}
